import { useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '@clerk/clerk-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Camera, Loader2, Pencil, Trash2 } from 'lucide-react'
import type { TFunction } from 'i18next'

import {
  LiveAppointmentTile,
  type LiveAppointmentTileEntry,
} from '@/components/appointments/LiveAppointmentTile'
import {
  CreatedByTooltip,
  type CreatedByInfo,
} from '@/components/CreatedByTooltip'
import { DeleteRecordDialog } from '@/components/DeleteRecordDialog'
import { EditableTags } from '@/components/EditableTags'
import { VinChip } from '@/components/VinChip'
import { MileageHistoryPanel } from '@/components/vehicles/MileageHistoryPanel'
import { OdometerPhotoUploadModal } from '@/components/vehicles/OdometerPhotoUploadModal'
import { VehicleDialog } from '@/components/vehicles/VehicleDialog'
import { VinPhotoUploadModal } from '@/components/vehicles/VinPhotoUploadModal'
import { Button } from '@/components/ui/button'
import { PushToTalkMic } from '@/components/voice/PushToTalkMic'
import { useCanApproveEstimate, useCanDeleteRecords } from '@/lib/auth'
import { deleteBlockerMessage, type DeleteErrorBody } from '@/lib/deleteRecord'
import { formatDate } from '@/lib/dates'
import { formatMoney } from '@/lib/format'
import type { Tag } from '@/lib/tags'

// Soft cap from Notion spec ("Per-vehicle notes — Q2"). 2000 chars
// keeps the notes readable in the appointment chip; the worker hard
// limit is 10k. Over the soft cap we render a hint but still allow
// save — the cap is UX guidance, not a server-side reject.
const NOTES_SOFT_CAP = 2000

type Vehicle = {
  id: string
  customer_id: string | null
  year: number | null
  make: string | null
  model: string | null
  trim: string | null
  engine: string | null
  drivetrain: string | null
  body: string | null
  vin: string | null
  plate: string | null
  mileage: number | null
  color: string | null
  notes: string | null
  notes_updated_at: string | null
  notes_updated_by: string | null
  created_at: string | null
  created_by: CreatedByInfo
}

type Owner = {
  id: string
  name: string
}

type ServiceHistoryRow = {
  id: string
  service_date: string
  service: string
  total_cad: number
  notes: string | null
}

type AppointmentSurfaceRow = {
  id: string
  starts_at: string
  ends_at: string
  status: string
  notes: string | null
  customer_id: string | null
  customer_name: string | null
}

type CompletedAppointmentRow = {
  id: string
  starts_at: string
  ends_at: string
  updated_at: string | null
  notes: string | null
  customer_id: string | null
  customer_name: string | null
}

type ServiceEntry = {
  key: string
  source: 'history' | 'appointment'
  sortKey: string
  date: string
  label: string
  amount: string
}

type VehicleDetailResponse = {
  vehicle: Vehicle
  owner: Owner | null
  service_history: ServiceHistoryRow[]
  live_appointments?: AppointmentSurfaceRow[]
  upcoming_appointments?: AppointmentSurfaceRow[]
  completed_appointments?: CompletedAppointmentRow[]
  // Added by tags-v1 — see CustomerDetail for the symmetric shape.
  tag_chips?: Tag[]
}

function buildServiceEntries(
  detail: VehicleDetailResponse,
  fallbackLabel: string,
): ServiceEntry[] {
  const entries: ServiceEntry[] = []

  for (const sh of detail.service_history) {
    entries.push({
      key: `sh-${sh.id}`,
      source: 'history',
      sortKey: sh.service_date,
      date: sh.service_date,
      label: sh.service,
      amount: formatMoney(sh.total_cad),
    })
  }

  for (const appt of detail.completed_appointments ?? []) {
    // Prefer updated_at (when status flipped to 'completed') over
    // ends_at; matches the customer-detail timeline rule.
    const at = appt.updated_at ?? appt.ends_at
    entries.push({
      key: `appt-${appt.id}`,
      source: 'appointment',
      sortKey: at,
      date: at,
      label: appt.notes?.trim() ? appt.notes : fallbackLabel,
      amount: '',
    })
  }

  entries.sort((a, b) => b.sortKey.localeCompare(a.sortKey))
  return entries
}

function toTileEntry(row: AppointmentSurfaceRow): LiveAppointmentTileEntry {
  return {
    id: row.id,
    starts_at: row.starts_at,
    ends_at: row.ends_at,
    status: row.status,
    notes: row.notes,
    customer_label: row.customer_name,
  }
}

function vehicleTitle(t: TFunction<'vehicles'>, v: Vehicle): string {
  return (
    [v.year, v.make, v.model].filter(Boolean).join(' ') ||
    t('detail.title_fallback')
  )
}

function vehicleSubtitle(v: Vehicle): string {
  return [v.engine, v.drivetrain, v.body].filter(Boolean).join(' · ')
}

function formatMileage(t: TFunction<'common'>, km: number | null): string {
  if (km === null || km === undefined) return '—'
  return t('units.km', { value: km.toLocaleString() })
}

export function VehicleDetail() {
  const { t } = useTranslation('vehicles')
  const { t: tCommon } = useTranslation('common')
  const { id: vehicleId } = useParams<{ id: string }>()
  const { getToken, isLoaded } = useAuth()
  const [editOpen, setEditOpen] = useState(false)
  const [vinPhotoOpen, setVinPhotoOpen] = useState(false)
  const [odometerPhotoOpen, setOdometerPhotoOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const navigate = useNavigate()
  const qc = useQueryClient()
  const canDelete = useCanDeleteRecords()
  // OWNER / ADMIN / MECHANIC — same gate as estimate approval. Receptionists
  // see the vehicle but can't attach a VIN or update mileage via the photo
  // flow (matches the requireRole on the corresponding worker routes).
  const canAddVinFromPhoto = useCanApproveEstimate()
  const canUpdateMileageFromPhoto = canAddVinFromPhoto

  const detailQuery = useQuery({
    queryKey: ['vehicle', vehicleId],
    enabled: isLoaded && !!vehicleId,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))

      const res = await fetch(`/api/vehicles/${vehicleId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (res.status === 404) return null
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }

      return (await res.json()) as VehicleDetailResponse
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))

      const res = await fetch(`/api/vehicles/${vehicleId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })

      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as DeleteErrorBody
        const blocked = deleteBlockerMessage(t, res.status, body)
        if (blocked) throw new Error(blocked)
        throw new Error(tCommon('error.request_failed', { status: res.status }))
      }

      return (await res.json()) as { ok: true }
    },
    onSuccess: () => {
      const ownerId = detailQuery.data?.owner?.id
      qc.invalidateQueries({ queryKey: ['vehicles'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      if (ownerId) qc.invalidateQueries({ queryKey: ['customer', ownerId] })
      navigate('/vehicles')
    },
    onError: (err) => {
      setDeleteError(
        err instanceof Error ? err.message : t('detail.delete.error'),
      )
    },
  })

  if (detailQuery.isLoading) {
    return (
      <div className="rounded-[14px] border border-border bg-background p-12 text-center text-sm text-muted-foreground">
        {tCommon('loading')}
      </div>
    )
  }

  if (detailQuery.isError) {
    return (
      <div className="rounded-[14px] border border-destructive/20 bg-destructive-soft p-4 text-sm text-destructive">
        {detailQuery.error instanceof Error
          ? detailQuery.error.message
          : t('detail.error')}
      </div>
    )
  }

  if (!detailQuery.data) {
    return (
      <div className="space-y-4">
        <Link
          to="/vehicles"
          className="inline-flex min-h-11 items-center gap-1 text-sm text-muted-foreground transition-colors active:opacity-70 [@media(hover:hover)_and_(pointer:fine)]:hover:text-foreground"
        >
          {t('detail.back')}
        </Link>
        <div className="rounded-[14px] border border-border bg-background p-12 text-center text-sm text-muted-foreground">
          {t('detail.not_found')}
        </div>
      </div>
    )
  }

  const { vehicle, owner } = detailQuery.data
  const serviceEntries = buildServiceEntries(
    detailQuery.data,
    t('detail.service_history_appointment_fallback'),
  )

  return (
    <div className="space-y-6">
      {/* §4.5 detail-page header: 44px back-button row above the title. */}
      <Link
        to="/vehicles"
        className="inline-flex min-h-11 items-center gap-1 text-sm text-muted-foreground transition-colors active:opacity-70 [@media(hover:hover)_and_(pointer:fine)]:hover:text-foreground"
      >
        {t('detail.back')}
      </Link>

      {/* Header card.
       *
       * Layout per the Notion spec ("Per-vehicle notes + voice
       * capture"): attribute grid on the LEFT half, persistent
       * notes editor on the RIGHT half (vertically aligned). On
       * mobile, notes collapse below the grid full-width.
       */}
      <div className="rounded-[14px] border border-border bg-background p-5 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-[22px] font-bold tracking-[-0.015em] text-foreground [text-wrap:balance]">
                {vehicleTitle(t, vehicle)}
              </h1>
              <CreatedByTooltip
                createdAt={vehicle.created_at}
                createdBy={vehicle.created_by}
              />
            </div>
            {vehicleSubtitle(vehicle) ? (
              <div className="mt-1 text-[13px] text-muted-foreground">
                {vehicleSubtitle(vehicle)}
              </div>
            ) : null}
            <EditableTags
              entityType="vehicle"
              entityId={vehicle.id}
              tags={detailQuery.data.tag_chips ?? []}
              className="mt-2"
            />
          </div>
          <div className="flex shrink-0 gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setEditOpen(true)}
            >
              <Pencil className="size-3.5" />
              {t('detail.edit')}
            </Button>
            {canDelete ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="text-destructive [@media(hover:hover)_and_(pointer:fine)]:hover:bg-destructive-soft [@media(hover:hover)_and_(pointer:fine)]:hover:text-destructive"
                onClick={() => {
                  setDeleteError(null)
                  setDeleteOpen(true)
                }}
              >
                <Trash2 className="size-3.5" />
                {tCommon('button.delete')}
              </Button>
            ) : null}
          </div>
        </div>

        <VehicleDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          seed={{
            id: vehicle.id,
            customer_id: vehicle.customer_id,
            vin: vehicle.vin,
            year: vehicle.year,
            make: vehicle.make,
            model: vehicle.model,
            trim: vehicle.trim,
            engine: vehicle.engine,
            drivetrain: vehicle.drivetrain,
            body: vehicle.body,
            color: vehicle.color,
            plate: vehicle.plate,
            mileage: vehicle.mileage,
          }}
        />

        <DeleteRecordDialog
          open={deleteOpen}
          onOpenChange={(next) => {
            setDeleteOpen(next)
            if (!next) setDeleteError(null)
          }}
          title={t('detail.delete.title')}
          description={
            <>
              {t('detail.delete.description', {
                vehicle: vehicleTitle(t, vehicle),
              })}
              <span className="mt-1 block">
                {t('detail.delete.cascade_note')}
              </span>
            </>
          }
          confirmLabel={t('detail.delete.confirm')}
          pending={deleteMutation.isPending}
          errorMessage={deleteError}
          onConfirm={() => {
            setDeleteError(null)
            deleteMutation.mutate()
          }}
        />
        <VinPhotoUploadModal
          vehicleId={vehicle.id}
          open={vinPhotoOpen}
          onOpenChange={setVinPhotoOpen}
        />
        <OdometerPhotoUploadModal
          vehicleId={vehicle.id}
          currentMileage={vehicle.mileage}
          open={odometerPhotoOpen}
          onOpenChange={setOdometerPhotoOpen}
        />

        <div className="mt-4 grid gap-6 md:grid-cols-2">
          {/* LEFT half: attribute grid. MILEAGE + VIN top row, then
              PLATE / COLOR, then OWNER spanning the row. */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-[12px] font-medium text-muted-foreground">
                {t('detail.mileage_label')}
              </div>
              <div className="font-mono text-[22px] font-bold tabular-nums text-foreground">
                {formatMileage(tCommon, vehicle.mileage)}
              </div>
              {canUpdateMileageFromPhoto ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setOdometerPhotoOpen(true)}
                  className="mt-1 h-8"
                >
                  <Camera className="size-3.5" />
                  {t('detail.update_mileage_from_photo')}
                </Button>
              ) : null}
            </div>
            <div>
              <div className="text-[12px] font-medium text-muted-foreground">
                {t('detail.specs.vin')}
              </div>
              {vehicle.vin ? (
                <div className="mt-0.5 tabular-nums">
                  <VinChip vin={vehicle.vin} />
                </div>
              ) : canAddVinFromPhoto ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setVinPhotoOpen(true)}
                  className="mt-1 h-8"
                >
                  <Camera className="size-3.5" />
                  {t('detail.add_vin_from_photo')}
                </Button>
              ) : (
                <div className="font-mono text-xs text-ink-3">—</div>
              )}
            </div>
            <div>
              <div className="text-[12px] font-medium text-muted-foreground">
                {t('detail.specs.plate')}
              </div>
              <div className="font-mono tabular-nums text-foreground">{vehicle.plate ?? '—'}</div>
            </div>
            <div>
              <div className="text-[12px] font-medium text-muted-foreground">
                {t('detail.specs.color')}
              </div>
              <div className="text-foreground">{vehicle.color ?? '—'}</div>
            </div>
            <div className="col-span-2">
              <div className="text-[12px] font-medium text-muted-foreground">
                {t('detail.specs.owner')}
              </div>
              {owner ? (
                <Link
                  to={`/customers/${owner.id}`}
                  className="font-medium text-foreground underline underline-offset-2"
                >
                  {owner.name}
                </Link>
              ) : (
                <span className="text-ink-3">—</span>
              )}
            </div>
          </div>

          {/* RIGHT half (md+): inline-editable notes with voice
              capture. On mobile this falls below the attribute grid
              full-width via the parent grid's single-column default. */}
          <VehicleNotesCard vehicle={vehicle} />
        </div>
      </div>

      {/* Live + upcoming appointment surface */}
      {(detailQuery.data.live_appointments?.length ?? 0) > 0 ||
      (detailQuery.data.upcoming_appointments?.length ?? 0) > 0 ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {detailQuery.data.live_appointments?.map((row) => (
            <LiveAppointmentTile
              key={row.id}
              appointment={toTileEntry(row)}
              kind="live"
            />
          ))}
          {detailQuery.data.upcoming_appointments?.map((row) => (
            <LiveAppointmentTile
              key={row.id}
              appointment={toTileEntry(row)}
              kind="upcoming"
            />
          ))}
        </div>
      ) : null}

      {/* Mileage history — append-only odometer timeline. The card on
          the left of the header shows only the latest cached value
          (vehicles.mileage); this panel is the full provenance trail. */}
      <MileageHistoryPanel vehicleId={vehicle.id} />

      {/* Service history */}
      <div>
        <h2 className="mb-2 text-[13px] font-semibold text-muted-foreground">
          {t('detail.service_history_header')}
        </h2>
        <div className="rounded-[14px] border border-border bg-background">
          {serviceEntries.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-muted-foreground">
              {t('detail.service_history_empty')}
            </div>
          ) : (
            <div className="divide-y divide-border">
              {serviceEntries.map((entry) => (
                <div
                  key={entry.key}
                  className="flex flex-col gap-2 px-4 py-3 text-sm sm:flex-row sm:items-center sm:gap-4"
                >
                  <div className="font-mono text-xs tabular-nums text-ink-3 sm:w-24 sm:shrink-0">
                    {formatDate(entry.date)}
                  </div>
                  <div className="min-w-0 flex-1 text-foreground">{entry.label}</div>
                  <div className="text-left font-mono font-semibold tabular-nums text-foreground sm:shrink-0 sm:text-right">
                    {entry.amount}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Persistent vehicle notes — inline editor with voice capture.
//
// Lives inside the header card's right half (md+) or stacked below the
// attribute grid (mobile). Display mode shows the saved string with a
// pencil affordance; clicking the empty target or the pencil opens
// the editor with a Textarea, push-to-talk mic, char counter, and
// explicit Save / Cancel buttons. Never auto-saves on speech-end —
// the spec explicitly calls this out (avoids saving mid-thought).
//
// Eric writes here too via the update_vehicle_notes(mode) brain tool —
// the footer line shows whether the last change came from Eric or
// from a human.
function VehicleNotesCard({
  vehicle,
}: {
  vehicle: Pick<
    Vehicle,
    'id' | 'notes' | 'notes_updated_at' | 'notes_updated_by' | 'created_by'
  >
}) {
  const { t } = useTranslation('vehicles')
  const { t: tCommon } = useTranslation('common')
  const { getToken } = useAuth()
  const queryClient = useQueryClient()

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(vehicle.notes ?? '')
  const [saveError, setSaveError] = useState<string | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  // If Eric pushes a notes update via the brain tools while this
  // component is mounted, the parent query refetches and the vehicle
  // prop changes. Sync the draft so we don't show stale text when the
  // editor next opens — but only when not actively editing, so we
  // never clobber the user's in-flight typing. Pattern A (You Might
  // Not Need an Effect): snapshot the previous notes and in-render
  // setState when it shifts while not editing.
  const [prevNotes, setPrevNotes] = useState(vehicle.notes ?? null)
  if ((vehicle.notes ?? null) !== prevNotes) {
    setPrevNotes(vehicle.notes ?? null)
    if (!editing) setDraft(vehicle.notes ?? '')
  }

  const saveMutation = useMutation({
    mutationFn: async (next: string): Promise<void> => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch(`/api/vehicles/${vehicle.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ notes: next }),
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(text || t('detail.notes.save_error'))
      }
    },
    onSuccess: () => {
      setEditing(false)
      setSaveError(null)
      // Refetch vehicle so the footer "Last updated …" timestamp and
      // updated_by reflect the write we just made.
      queryClient.invalidateQueries({ queryKey: ['vehicle', vehicle.id] })
      // Appointment surfaces (chip + tile badge) read vehicle.notes
      // off the cached appointments list — bust that so they pick up
      // the change without a hard refresh.
      queryClient.invalidateQueries({ queryKey: ['appointments'] })
    },
    onError: (err) => {
      setSaveError(
        err instanceof Error ? err.message : t('detail.notes.save_error'),
      )
    },
  })

  const startEditing = () => {
    setDraft(vehicle.notes ?? '')
    setSaveError(null)
    setEditing(true)
    // Focus the textarea on next tick so the caret is ready to type.
    setTimeout(() => textareaRef.current?.focus(), 0)
  }

  const cancelEditing = () => {
    setDraft(vehicle.notes ?? '')
    setSaveError(null)
    setEditing(false)
  }

  const handleVoiceTranscript = (text: string) => {
    setDraft((prev) => {
      const trimmed = prev.trim()
      if (trimmed.length === 0) return text
      return `${trimmed} ${text}`
    })
    // Push focus back to the textarea so the user can immediately
    // edit the appended transcript if they need to.
    setTimeout(() => {
      const el = textareaRef.current
      if (!el) return
      el.focus()
      el.setSelectionRange(el.value.length, el.value.length)
    }, 0)
  }

  const charCount = draft.length
  const overSoftCap = charCount > NOTES_SOFT_CAP
  const filled = (vehicle.notes ?? '').trim().length > 0

  const footer = vehicle.notes_updated_at ? (
    <div className="mt-2 text-[12px] text-ink-3">
      {t(
        vehicle.notes_updated_by === 'eric' ||
          vehicle.created_by?.agent === 'eric'
          ? 'detail.notes.footer_updated_by_eric'
          : 'detail.notes.footer_updated_by_human',
        { when: formatDate(vehicle.notes_updated_at) },
      )}
    </div>
  ) : null

  // ── Edit mode ─────────────────────────────────────────────────────
  if (editing) {
    return (
      <div className="flex flex-col">
        <div className="mb-2 flex items-center justify-between gap-2">
          <div className="text-[12px] font-medium text-muted-foreground">
            {t('detail.notes.header')}
          </div>
          <PushToTalkMic
            onTranscript={handleVoiceTranscript}
            disabled={saveMutation.isPending}
          />
        </div>
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={5}
          maxLength={10_000}
          placeholder={t('detail.notes.empty_clickable')}
          disabled={saveMutation.isPending}
          className="flex min-h-20 w-full resize-y rounded-[10px] border border-input bg-background px-2.5 py-2 text-base outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-60 md:text-sm"
        />
        <div className="mt-1 flex items-center justify-between gap-2 text-[12px] text-ink-3">
          <span>{t('detail.notes.char_counter', { count: charCount })}</span>
          {overSoftCap ? (
            <span className="text-attention-text">
              {t('detail.notes.over_limit_warning')}
            </span>
          ) : null}
        </div>
        {saveError ? (
          <div className="mt-2 rounded-[10px] border border-destructive/20 bg-destructive-soft p-2 text-xs text-destructive">
            {saveError}
          </div>
        ) : null}
        <div className="mt-3 flex items-center justify-end gap-2">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={cancelEditing}
            disabled={saveMutation.isPending}
          >
            {t('detail.notes.cancel')}
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => saveMutation.mutate(draft)}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? (
              <>
                <Loader2 className="size-3.5 animate-spin" />
                {t('detail.notes.saving')}
              </>
            ) : (
              t('detail.notes.save')
            )}
          </Button>
        </div>
      </div>
    )
  }

  // ── Display mode ──────────────────────────────────────────────────
  return (
    <div className="group flex flex-col">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="text-[12px] font-medium text-muted-foreground">
          {t('detail.notes.header')}
        </div>
        <button
          type="button"
          onClick={startEditing}
          aria-label={t('detail.notes.edit')}
          title={t('detail.notes.edit')}
          className="rounded-[10px] p-1 text-ink-3 opacity-0 transition-[background-color,color,opacity] focus-visible:opacity-100 group-hover:opacity-100 md:opacity-0 [@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted [@media(hover:hover)_and_(pointer:fine)]:hover:text-foreground"
        >
          <Pencil className="size-3.5" />
        </button>
      </div>
      {filled ? (
        <button
          type="button"
          onClick={startEditing}
          className="rounded-[10px] border border-transparent bg-transparent p-0 text-left text-sm text-foreground focus-visible:border-border focus-visible:bg-muted focus-visible:outline-none [@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted"
        >
          <p className="whitespace-pre-wrap px-2 py-1.5">{vehicle.notes}</p>
        </button>
      ) : (
        <button
          type="button"
          onClick={startEditing}
          className="flex min-h-[80px] items-center justify-center rounded-[10px] border border-dashed border-border bg-muted/40 px-3 py-4 text-center text-xs text-muted-foreground focus-visible:border-input focus-visible:outline-none [@media(hover:hover)_and_(pointer:fine)]:hover:border-input [@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted [@media(hover:hover)_and_(pointer:fine)]:hover:text-foreground"
        >
          {t('detail.notes.empty_clickable')}
        </button>
      )}
      {footer}
    </div>
  )
}
